import { useMemo } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { IconButton } from '@/components/ui/icon-button';
import { ToggleChip } from '@/components/ui/toggle-chip';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Trace } from '@/features/traceability/type/Tool';
import type { FilterRule, FilterOperator } from '../../type/dynamicChart';
import { filterDimensionRegistry } from '../../utils/filterDimensions';
import { CategoricalValueInput } from './CategoricalValueInput';

type FilterRuleRowProps = {
  rule: FilterRule;
  traces: (Trace & { toolId: string })[];
  onChange: (updated: FilterRule) => void;
  onRemove: () => void;
};

export function FilterRuleRow({ rule, traces, onChange, onRemove }: FilterRuleRowProps) {
  const { t } = useTranslation();

  const dimDef = filterDimensionRegistry.find((d) => d.key === rule.dimension);
  const kind = dimDef?.kind ?? 'categorical';

  const badgeLabel = t(`analytics.dashboard.complexCharts.filters.kindBadge.${kind}`);

  // Available values for categorical dimensions
  const availableValues = useMemo(() => {
    if (kind !== 'categorical' || !dimDef) return [];
    const vals = new Set<string>();
    traces.forEach((trace) => {
      const v = dimDef.extract(trace);
      if (v !== null && v !== undefined) vals.add(String(v));
    });
    return Array.from(vals).sort();
  }, [traces, dimDef, kind]);

  // Build operator options for this dimension
  const operatorOptions: { value: string; label: string }[] = [];
  if (dimDef) {
    dimDef.operators.forEach((op) => {
      operatorOptions.push({
        value: op,
        label: t(`analytics.dashboard.complexCharts.filters.operators.${op}`),
      });
    });
    if (kind === 'categorical') {
      operatorOptions.push({
        value: 'not_in',
        label: t('analytics.dashboard.complexCharts.filters.operators.not_in'),
      });
    }
  }

  const handleOperatorChange = (val: string) => {
    if (val === 'not_in') {
      onChange({ ...rule, operator: 'in', negate: true });
    } else {
      onChange({ ...rule, operator: val as FilterOperator, negate: false });
    }
  };

  // Current select value: if operator is 'in' and negate → show 'not_in'
  const selectValue = rule.operator === 'in' && rule.negate ? 'not_in' : rule.operator;

  const isRangeError =
    rule.operator === 'range' &&
    rule.values[0] !== undefined &&
    rule.values[1] !== undefined &&
    rule.values[0] !== '' &&
    rule.values[1] !== '' &&
    parseFloat(rule.values[0]) > parseFloat(rule.values[1]);

  const unit = dimDef?.unit;
  const dimLabel = dimDef ? t(dimDef.labelKey) : rule.dimension;

  return (
    <div
      className="flex flex-wrap items-start gap-2 rounded-lg border border-border bg-muted/30 p-2"
    >
      {/* Remove button */}
      <IconButton
        icon={X}
        label={t('analytics.dashboard.complexCharts.filters.removeRule')}
        onClick={onRemove}
      />

      {/* Kind badge */}
      <span
        className="mt-1 shrink-0 rounded px-1.5 py-0.5 font-mono text-xs font-medium uppercase tracking-widest bg-muted text-foreground"
      >
        {badgeLabel}
      </span>

      {/* Dimension label */}
      <span className="mt-1 shrink-0 text-xs font-medium text-foreground">{dimLabel}</span>

      {/* Operator select */}
      <Select value={selectValue} onValueChange={handleOperatorChange}>
        <SelectTrigger size="sm" className="h-7 min-w-[90px] max-w-[130px] text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operatorOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-xs">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value inputs */}
      <div className="flex flex-1 flex-col gap-1">
        {kind === 'categorical' && (
          <CategoricalValueInput
            rule={rule}
            availableValues={availableValues}
            onChange={onChange}
          />
        )}

        {kind === 'numeric' && rule.operator === 'range' && (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className={`h-7 w-20 text-xs ${isRangeError ? 'border-destructive' : ''}`}
              value={rule.values[0] ?? ''}
              onChange={(e) => onChange({ ...rule, values: [e.target.value, rule.values[1] ?? ''] })}
              placeholder={t('analytics.dashboard.complexCharts.tooltip.min')}
            />
            <span className="text-xs text-muted-foreground">—</span>
            <Input
              type="number"
              className={`h-7 w-20 text-xs ${isRangeError ? 'border-destructive' : ''}`}
              value={rule.values[1] ?? ''}
              onChange={(e) => onChange({ ...rule, values: [rule.values[0] ?? '', e.target.value] })}
              placeholder={t('analytics.dashboard.complexCharts.tooltip.max')}
            />
            {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
            {isRangeError && (
              <span className="text-xs text-destructive">
                {t('analytics.dashboard.complexCharts.filters.minMaxError')}
              </span>
            )}
          </div>
        )}

        {kind === 'numeric' && (rule.operator === 'gte' || rule.operator === 'lte') && (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="h-7 w-20 text-xs"
              value={rule.values[0] ?? ''}
              onChange={(e) => onChange({ ...rule, values: [e.target.value] })}
              placeholder={t('analytics.dashboard.complexCharts.tooltip.value')}
            />
            {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
          </div>
        )}

        {kind === 'date' && rule.operator === 'date_range' && (
          <div className="flex items-center gap-1">
            <Input
              type="date"
              className="h-7 w-20 text-xs"
              value={rule.values[0] ?? ''}
              onChange={(e) => onChange({ ...rule, values: [e.target.value, rule.values[1] ?? ''] })}
            />
            <span className="text-xs text-muted-foreground">—</span>
            <Input
              type="date"
              className="h-7 w-20 text-xs"
              value={rule.values[1] ?? ''}
              onChange={(e) => onChange({ ...rule, values: [rule.values[0] ?? '', e.target.value] })}
            />
          </div>
        )}

        {kind === 'date' && (rule.operator === 'after' || rule.operator === 'before') && (
          <div className="flex items-center gap-1">
            <Input
              type="date"
              className="h-7 w-20 text-xs"
              value={rule.values[0] ?? ''}
              onChange={(e) => onChange({ ...rule, values: [e.target.value] })}
            />
          </div>
        )}
      </div>

      {/* Negate toggle */}
      <ToggleChip
        pressed={rule.negate}
        aria-label={t('analytics.dashboard.complexCharts.filters.negateRule')}
        title={t('analytics.dashboard.complexCharts.filters.negateTitle')}
        onClick={() => onChange({ ...rule, negate: !rule.negate })}
        className="shrink-0"
      >
        ≠
      </ToggleChip>
    </div>
  );
}
